import type { TechnicalPassportDimensions } from './types'

/** The seven centimetre measurements the backend derives the mm dimensions from. */
export const FIXED_MEASUREMENT_KEYS = [
  'rezinka_eni_sm',
  'rezinka_boyi_sm',
  'tovondan_uchiga_sm',
  'tovondan_rezinkaga_sm',
  'eni_sm',
  'eni_plotnost_sm',
  'uzunligi_plotnost_sm',
] as const

export type FixedMeasurementKey = (typeof FIXED_MEASUREMENT_KEYS)[number]
export type FixedMeasurements = Record<FixedMeasurementKey, number | null>

export function emptyFixedMeasurements(): FixedMeasurements {
  return {
    rezinka_eni_sm: null,
    rezinka_boyi_sm: null,
    tovondan_uchiga_sm: null,
    tovondan_rezinkaga_sm: null,
    eni_sm: null,
    eni_plotnost_sm: null,
    uzunligi_plotnost_sm: null,
  }
}

export function normalizeFixedMeasurements(value: unknown): FixedMeasurements {
  const source = (value && typeof value === 'object' ? value : {}) as Record<string, unknown>
  const result = emptyFixedMeasurements()
  for (const key of FIXED_MEASUREMENT_KEYS) {
    const numeric = Number(source[key])
    result[key] = source[key] == null || source[key] === '' || !Number.isFinite(numeric)
      ? null
      : numeric
  }
  return result
}

const TOE_RATIO = 0.18
const HEEL_RATIO = 0.24
const INSTEP_RATIO = 0.35

/**
 * Mirrors `TechnicalPassportRules::deriveDimensions()` so the drawing reacts to
 * inputs before the passport is saved. All input values are centimetres.
 */
export function deriveSockDimensions(fixed: Partial<Record<FixedMeasurementKey, number | null>>): TechnicalPassportDimensions {
  const numeric = (value: number | null | undefined): number | null => {
    const parsed = Number(value)
    return value == null || !Number.isFinite(parsed) ? null : parsed
  }
  const footLengthMm = toMillimetres(numeric(fixed.tovondan_uchiga_sm))
  const legHeightMm = toMillimetres(numeric(fixed.tovondan_rezinkaga_sm))
  return {
    foot_length_mm: footLengthMm,
    leg_height_mm: legHeightMm,
    foot_width_mm: toMillimetres(numeric(fixed.eni_sm)),
    cuff_height_mm: toMillimetres(numeric(fixed.rezinka_boyi_sm)),
    cuff_width_mm: toMillimetres(numeric(fixed.rezinka_eni_sm)),
    toe_length_mm: derivedLength(footLengthMm, TOE_RATIO),
    heel_length_mm: derivedLength(footLengthMm, HEEL_RATIO),
    instep_length_mm: derivedLength(legHeightMm, INSTEP_RATIO),
  }
}

function toMillimetres(centimetres: number | null): number | null {
  return centimetres === null ? null : centimetres * 10
}

function derivedLength(baseMillimetres: number | null, ratio: number): number | null {
  return baseMillimetres === null ? null : Math.round(baseMillimetres * ratio * 100) / 100
}

/** Recover the cm inputs for legacy passports that only stored the mm dimensions. */
export function fixedMeasurementsFromDimensions(
  dimensions?: TechnicalPassportDimensions | null,
): FixedMeasurements {
  const cm = (millimetres?: number | null): number | null => {
    const numeric = Number(millimetres)
    return millimetres == null || !Number.isFinite(numeric) || numeric <= 0
      ? null
      : Math.round((numeric / 10) * 100) / 100
  }
  return {
    rezinka_eni_sm: cm(dimensions?.cuff_width_mm),
    rezinka_boyi_sm: cm(dimensions?.cuff_height_mm),
    tovondan_uchiga_sm: cm(dimensions?.foot_length_mm),
    tovondan_rezinkaga_sm: cm(dimensions?.leg_height_mm),
    eni_sm: cm(dimensions?.foot_width_mm),
    eni_plotnost_sm: null,
    uzunligi_plotnost_sm: null,
  }
}
